import React, { useEffect, useRef } from 'react';
import { useServices } from '../lib/services';

/**
 * The row of streaming services that scopes browsing to one of them.
 *
 * Shared by Home, Films and Series, so a choice made on one is visibly the
 * same choice on the others. "All" leads the row and is how a choice is
 * cleared. The chosen tile scrolls itself into view, so a service far down a
 * phone-width row is never selected-but-hidden.
 */
const ServiceSwitcher = ({ value, onChange, title = 'Streaming services' }) => {
  const { services, regionName, find } = useServices();
  const row = useRef(null);

  useEffect(() => {
    const active = row.current?.querySelector('.svc.is-active');
    if (!active) return;
    // Only the row scrolls; the page stays where it is
    const box = row.current.getBoundingClientRect();
    const tile = active.getBoundingClientRect();
    if (tile.left < box.left || tile.right > box.right) {
      row.current.scrollBy({ left: tile.left - box.left - box.width / 2 + tile.width / 2, behavior: 'smooth' });
    }
  }, [value, services.length]);

  if (!services.length) return null;

  const current = value ? find(value) : null;
  // A service reached by link that the switcher doesn't list (a store, say)
  // still gets a tile, so the row never claims "All" while one is applied
  const tiles = current && !services.some((s) => String(s.id) === String(value)) ? [current, ...services] : services;

  return (
    <section className="svc-bar" aria-label={title}>
      <div className="svc-head">
        <h2 className="svc-title">{current ? current.name : title}</h2>
        <span className="svc-note">
          {current ? 'Only what’s included with a subscription' : 'Pick one to browse only its catalogue'}
          {regionName ? ` · ${regionName}` : ''}
        </span>
        {current && (
          <button type="button" className="svc-clear" onClick={() => onChange('')}>
            Show all services
          </button>
        )}
      </div>
      <div className="svc-row" ref={row} role="radiogroup" aria-label={title}>
        <button
          type="button"
          role="radio"
          aria-checked={!value}
          className={`svc svc--all${!value ? ' is-active' : ''}`}
          onClick={() => onChange('')}
        >
          <span className="svc-tile">All</span>
          <span className="svc-name">All</span>
        </button>
        {tiles.map((service) => {
          const active = String(service.id) === String(value);
          return (
            <button
              key={service.id}
              type="button"
              role="radio"
              aria-checked={active}
              className={`svc${active ? ' is-active' : ''}`}
              title={service.name}
              onClick={() => onChange(active ? '' : String(service.id))}
            >
              <span className="svc-tile">
                {service.logo ? <img src={service.logo} alt="" loading="lazy" /> : service.name.slice(0, 2)}
              </span>
              <span className="svc-name">{service.name}</span>
            </button>
          );
        })}
      </div>
    </section>
  );
};

export default ServiceSwitcher;
